/**
 * TavernFloor — the room's CHAT layer over the live 3D backdrop.
 *
 * The old 2D top-down "walking floor" (an SVG plank floor + player avatars
 * strolling between bar/table icons) is GONE: RoomBackdrop3D now seats every
 * live player as their cosmetic-matched 3D character behind this UI, so the 2D
 * walking visual was redundant. What remains here is the SOCIAL layer that the
 * 3D scene can't host: chat as speech bubbles, reactions as single-glyph
 * bubbles, and a docked composer that's always reachable on mobile (no scroll).
 *
 * Speech / reaction bubbles render as a bottom-anchored feed that floats over
 * the 3D backdrop; each is attributed to its speaker (icon + name, tap to
 * inspect). Bubbles spawn React-driven on new chat/reactions and auto-expire.
 * Ready / host CONTROLS live in Room.tsx (the tankard + bell actions); the
 * per-player ready/host state shows in the 3D seating + the chalkboard tally.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LobbyView } from '@tt/lobby';
import { IconAvatar } from '../characters/IconAvatar';
import type { ChatMsg, Reaction } from './useLobbySocket';

type Player = LobbyView['players'][number];

interface Bubble {
  /** Unique key for React. */
  id: string;
  /** Owner — the speaker the bubble is attributed to. */
  charId: string;
  kind: 'chat' | 'reaction';
  text: string;
  /** ms epoch when the bubble should disappear. */
  until: number;
}

const CHAT_BUBBLE_MS = 6000;
const REACTION_BUBBLE_MS = 2200;

interface Props {
  players: Player[];
  /** Chat messages from useLobbySocket — bubbles spawn for new entries. */
  chat?: ChatMsg[];
  /** Reactions from useLobbySocket — single-glyph bubbles. */
  reactions?: Reaction[];
  /** Wired to sock.sendChat — composer pill submit handler. */
  onSendChat?: (text: string) => void;
  /** Wired to sock.sendReaction — emoji picker. */
  onSendReaction?: (emoji: string) => void;
  /** Reaction-picker emoji set, per the lobby's unlocked list. */
  reactionEmojis?: string[];
  /** When false, hide the composer (chat disabled in this lobby). */
  chatEnabled?: boolean;
  onInspectPlayer?: (charId: string) => void;
  /** Local player's charId — shown with a "me" rim. */
  meCharId?: string;
  /** 8P Phase 1: when the 3D hall anchors chat bubbles above speakers'
   *  heads, suppress this component's floating feed (the composer + the
   *  tap-to-open history stay — they're the input/archive surfaces). */
  bubblesInWorld?: boolean;
  /** Waiting Room 2.0: render as a single 💬 button (thumb-reachable in
   *  the bottom action bar) that pops the composer open above it —
   *  instead of the full-width docked composer strip. */
  compact?: boolean;
}

export function TavernFloor({
  players, chat, reactions, onSendChat, onSendReaction,
  reactionEmojis, chatEnabled = true, onInspectPlayer, meCharId,
  bubblesInWorld = false,
  compact = false,
}: Props) {
  const [composerOpen, setComposerOpen] = useState(false);
  // Fast charId → player lookup so bubbles can show the speaker's icon + name.
  const playerById = useMemo(() => {
    const m = new Map<string, Player>();
    for (const p of players) m.set(p.charId, p);
    return m;
  }, [players]);

  // Speech bubbles + reaction bubbles. Keyed by bubble.id. Bubbles auto-expire
  // by `until`; the feed shows the most recent few, newest at the bottom.
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  // Track which chat / reaction items we've already turned into bubbles so
  // reconnects / re-renders don't spawn duplicates.
  const lastChatKeyRef = useRef<string>('');
  const lastReactionIdRef = useRef<number>(0);
  // Per-charId timestamp of the LAST bubble we spawned, to rate-limit bubble
  // spawns to one per 3s per speaker so rapid chat bursts don't flood the feed.
  const lastBubbleAtRef = useRef<Map<string, number>>(new Map());
  const BUBBLE_RATE_LIMIT_MS = 3000;

  // Composer state.
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Spawn bubbles for new chat messages.
  useEffect(() => {
    if (!chat || chat.length === 0) return;
    const newest = chat[chat.length - 1]!;
    const key = `${newest.at}-${newest.charId}-${newest.text.slice(0, 16)}`;
    if (key === lastChatKeyRef.current) return;
    lastChatKeyRef.current = key;
    // Rate-limit per-speaker so spammy chat bursts don't flood the feed.
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()).slice(-5),
      { id, charId: newest.charId, kind: 'chat', text: newest.text.slice(0, 80), until: Date.now() + CHAT_BUBBLE_MS },
    ]);
  }, [chat]);

  // Spawn bubbles for new reactions.
  useEffect(() => {
    if (!reactions || reactions.length === 0) return;
    const newest = reactions[reactions.length - 1]!;
    if (newest.id === lastReactionIdRef.current) return;
    lastReactionIdRef.current = newest.id;
    const lastAt = lastBubbleAtRef.current.get(newest.charId) ?? 0;
    if (Date.now() - lastAt < BUBBLE_RATE_LIMIT_MS) return;
    lastBubbleAtRef.current.set(newest.charId, Date.now());
    const id = `r-${newest.id}`;
    setBubbles((prev) => [
      ...prev.filter((b) => b.until > Date.now()).slice(-5),
      { id, charId: newest.charId, kind: 'reaction', text: newest.emoji, until: Date.now() + REACTION_BUBBLE_MS },
    ]);
  }, [reactions]);

  // Periodically GC expired bubbles.
  useEffect(() => {
    if (bubbles.length === 0) return;
    const t = window.setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.until > Date.now()));
    }, 600);
    return () => window.clearTimeout(t);
  }, [bubbles]);

  if (players.length === 0) return null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t || !onSendChat) return;
    onSendChat(t);
    setDraft('');
  };

  const reactionPicks = (reactionEmojis && reactionEmojis.length > 0)
    ? reactionEmojis
    : ['👏', '🔥', '💀', '🎉', '😂', '😱'];

  // In-world bubbles (above heads in the hall) replace the floating feed.
  const visibleBubbles = bubblesInWorld ? [] : bubbles.slice(-4);

  // The composer form is shared by both layouts: the classic docked strip
  // and the Waiting-Room-2.0 compact popover.
  const composerForm = chatEnabled ? (
        <form className={`tavern-floor-composer${historyOpen ? ' history-open' : ''}${compact ? ' in-pop' : ''}`} onSubmit={onSubmit}>
          <button
            type="button"
            className="tavern-floor-composer-history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
          >
            {historyOpen ? '▼' : '▲'}
          </button>
          {historyOpen && chat && chat.length > 0 && (
            <div className="tavern-floor-history" role="log" aria-live="polite">
              {chat.slice(-12).map((m, i) => (
                <div key={i} className="tavern-floor-history-row">
                  <span className="tavern-floor-history-name">{m.name}:</span> <span>{m.text}</span>
                </div>
              ))}
            </div>
          )}
          <input
            type="text"
            className="tavern-floor-composer-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="say something…"
            maxLength={200}
            aria-label="Chat message"
          />
          <button
            type="button"
            className="tavern-floor-composer-react"
            onClick={() => setPickerOpen((v) => !v)}
            aria-label="Send a reaction"
            aria-expanded={pickerOpen}
          >
            ☺
          </button>
          {pickerOpen && (
            <div className="tavern-floor-react-picker" role="dialog" aria-label="Reactions">
              {reactionPicks.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="tavern-floor-react-glyph"
                  onClick={() => { onSendReaction?.(e); setPickerOpen(false); }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
          <button
            type="submit"
            className="tavern-floor-composer-send"
            disabled={!draft.trim()}
            aria-label="Send"
          >
            <span className="tavern-floor-composer-seal">✉</span>
          </button>
        </form>
  ) : null;

  // ── Waiting Room 2.0 compact mode: one thumb-height 💬 button in the
  // bottom action bar; the composer pops open above it. ──
  if (compact) {
    return (
      <div className="tavern-compact-chat">
        {chatEnabled && (
          <button
            type="button"
            className="tavern-chat-fab"
            onClick={() => setComposerOpen((v) => !v)}
            aria-expanded={composerOpen}
            aria-label={composerOpen ? 'Close chat' : 'Open chat'}
          >
            💬
          </button>
        )}
        {composerOpen && (
          <div className="tavern-compact-pop">{composerForm}</div>
        )}
      </div>
    );
  }

  return (
    <section className="tavern-floor" aria-label="tavern chat">
      {/* Speech / reaction bubble feed floating over the 3D backdrop. Newest
          at the bottom (nearest the composer). Empty until someone speaks. */}
      <div className="tavern-chat-feed" role="log" aria-live="polite" aria-label="recent chat">
        {visibleBubbles.map((b) => {
          const p = playerById.get(b.charId);
          const name = p?.name ?? 'someone';
          const isMe = meCharId === b.charId;
          return (
            <div
              key={b.id}
              className={`tavern-chat-row tavern-chat-row-${b.kind}${isMe ? ' is-me' : ''}`}
            >
              <button
                type="button"
                className="tavern-chat-author"
                onClick={() => onInspectPlayer?.(b.charId)}
                aria-label={`View ${name}`}
              >
                {p && <IconAvatar icon={p.icon} size={26} alt="" />}
                <span className="tavern-chat-author-name">{name}</span>
              </button>
              <span className={`tavern-chat-bubble tavern-chat-bubble-${b.kind}`}>
                {b.text}
              </span>
            </div>
          );
        })}
      </div>
      {composerForm}
      <div className="tavern-floor-tagline">at the tavern</div>
    </section>
  );
}
